import type { ErrorEvent } from "@sentry/react";

// Statuses the app already handles as control flow, so httpClientIntegration's
// re-capture is pure noise (see WEB-APP-R8, MOBILE-APP-8P):
//   401/403 -> AuthResponseInterceptor forces a logout
//   409     -> surfaced as a domain conflict (e.g. device already assigned)
const HANDLED_HTTP_CLIENT_STATUS_CODES = [401, 403, 409];

// @sentry/react (v9) tags these as "http.client"; @sentry/react-native and
// @sentry/browser v10 tag them as "auto.http.client.<xhr|fetch>".
function isHttpClientMechanism(type: string | undefined): boolean {
  return (
    type === "http.client" || (type?.startsWith("auto.http.client") ?? false)
  );
}

function getHttpClientStatus(event: ErrorEvent): number | undefined {
  const httpClient = event.exception?.values?.find(value =>
    isHttpClientMechanism(value.mechanism?.type),
  );
  if (!httpClient) {
    return undefined;
  }

  const responseContext = event.contexts?.response as
    | { status_code?: number }
    | undefined;
  if (typeof responseContext?.status_code === "number") {
    return responseContext.status_code;
  }

  const match = /status code:\s*(\d{3})/i.exec(httpClient.value ?? "");
  return match ? Number(match[1]) : undefined;
}

export function isHandledHttpClientFailureEvent(event: ErrorEvent): boolean {
  const status = getHttpClientStatus(event);
  return (
    status !== undefined && HANDLED_HTTP_CLIENT_STATUS_CODES.includes(status)
  );
}
